/**
 * PeakPricingDialog — Phase 178.6.G Part 4.
 *
 * Premium shadcn replacement for the slot-level `window.prompt` peak
 * pricing flow.  Backed by `POST /api/arenax/slots/{id}/peak-pricing`
 * which accepts either `multiplier` OR `price_override`.
 *
 *   • Mode toggle: multiplier vs absolute price
 *   • Live preview: base × multiplier → new price
 *   • Optional label ("Friday evening surge")
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { IndianRupee, Loader2, TrendingUp } from 'lucide-react';
import api from '@/lib/api';
import { track } from '@/lib/analytics';

const MULTIPLIER_PRESETS = [1.25, 1.5, 1.75, 2.0];

export default function PeakPricingDialog({ slot, open, onClose, onSuccess }) {
  const basePrice = Number(slot?.base_price || slot?.price || 0);
  const [mode, setMode] = useState('multiplier');  // 'multiplier' | 'absolute'
  const [multiplier, setMultiplier] = useState(1.5);
  const [absolutePrice, setAbsolutePrice] = useState('');
  const [label, setLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const previewPrice = useMemo(() => {
    if (mode === 'absolute') {
      const n = Number(absolutePrice);
      return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : null;
    }
    const m = Number(multiplier);
    if (!Number.isFinite(m) || m <= 0) return null;
    return Math.round(basePrice * m * 100) / 100;
  }, [mode, multiplier, absolutePrice, basePrice]);

  const delta = previewPrice != null ? previewPrice - basePrice : 0;
  const effectiveMult = basePrice > 0 && previewPrice != null
    ? Math.round((previewPrice / basePrice) * 100) / 100 : null;

  useEffect(() => {
    if (slot && open) {
      setMode('multiplier');
      setMultiplier(1.5);
      setAbsolutePrice('');
      setLabel('');
      track('dialog_opened', { dialog: 'slot_peak_pricing', slot_id: slot.id });
    }
  }, [slot, open]);

  if (!slot) return null;

  const handleSubmit = async () => {
    if (previewPrice == null) { toast.error('Enter a valid value'); return; }
    if (mode === 'multiplier') {
      const m = Number(multiplier);
      if (!Number.isFinite(m) || m <= 0 || m > 10) {
        toast.error('Multiplier must be > 0 and ≤ 10'); return;
      }
    }
    setSubmitting(true);
    try {
      const payload = mode === 'multiplier'
        ? { multiplier: Number(multiplier), label: label.trim() || null }
        : { price_override: Number(absolutePrice), label: label.trim() || null };
      const r = await api.post(`/arenax/slots/${slot.id}/peak-pricing`, payload);
      track('dialog_submitted', {
        dialog: 'slot_peak_pricing', slot_id: slot.id,
        idempotent: r.data?.idempotent, mode, multiplier: effectiveMult,
      });
      toast.success(r.data?.idempotent ? 'Same pricing — no change' :
        `Peak pricing applied · ₹${previewPrice}`);
      onSuccess?.(r.data);
      onClose?.();
    } catch (err) {
      track('dialog_failed', { dialog: 'slot_peak_pricing', slot_id: slot.id, error: err?.response?.status });
      toast.error(err?.response?.data?.detail || 'Could not apply peak pricing');
    } finally { setSubmitting(false); }
  };

  const handleCancel = () => {
    track('dialog_cancelled', { dialog: 'slot_peak_pricing', slot_id: slot?.id });
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleCancel()}>
      <DialogContent className="sm:max-w-md" data-testid="slot-peak-pricing-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" /> Peak pricing
          </DialogTitle>
          <DialogDescription className="leading-relaxed pt-1">
            Set a surcharge on this slot. Multipliers reprice based on the
            current base; absolute prices override directly.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-center gap-2 text-xs">
            <Button size="sm" type="button"
                    variant={mode === 'multiplier' ? 'default' : 'outline'}
                    onClick={() => setMode('multiplier')}
                    data-testid="slot-peak-mode-multiplier">
              Multiplier
            </Button>
            <Button size="sm" type="button"
                    variant={mode === 'absolute' ? 'default' : 'outline'}
                    onClick={() => setMode('absolute')}
                    data-testid="slot-peak-mode-absolute">
              Absolute price
            </Button>
          </div>

          {mode === 'multiplier' ? (
            <div>
              <Label className="text-xs uppercase tracking-wider">Multiplier</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {MULTIPLIER_PRESETS.map((m) => (
                  <Button key={m} size="sm" type="button"
                          variant={Number(multiplier) === m ? 'default' : 'outline'}
                          onClick={() => setMultiplier(m)}
                          data-testid={`slot-peak-preset-${String(m).replace('.', 'p')}`}>
                    ×{m}
                  </Button>
                ))}
              </div>
              <Input className="mt-2" type="number" step="0.05" min="0.1" max="10"
                     value={multiplier} onChange={(e) => setMultiplier(e.target.value)}
                     data-testid="slot-peak-multiplier-input" />
            </div>
          ) : (
            <div>
              <Label className="text-xs uppercase tracking-wider">Absolute price (₹)</Label>
              <Input className="mt-1.5" type="number" step="50" min="0"
                     placeholder={`Base ₹${basePrice.toFixed(0)}`}
                     value={absolutePrice} onChange={(e) => setAbsolutePrice(e.target.value)}
                     data-testid="slot-peak-absolute-input" />
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-wider">Label (optional)</Label>
            <Input className="mt-1" placeholder="e.g. Friday evening surge"
                   value={label} onChange={(e) => setLabel(e.target.value)}
                   maxLength={60}
                   data-testid="slot-peak-label-input" />
          </div>

          {/* Live preview */}
          <div
            className="rounded-lg border p-3 space-y-1.5"
            style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--muted) / 0.3)' }}
            data-testid="slot-peak-preview"
          >
            <div className="flex items-center justify-between text-xs"
                 style={{ color: 'hsl(var(--muted-foreground))' }}>
              <span>Base</span>
              <span className="inline-flex items-center font-mono">
                <IndianRupee className="h-3 w-3" />{basePrice.toFixed(0)}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs"
                 style={{ color: 'hsl(var(--muted-foreground))' }}>
              <span>Effective multiplier</span>
              <span className="font-mono">× {effectiveMult ?? '—'}</span>
            </div>
            <div className="flex items-center justify-between text-sm font-bold pt-1.5 border-t"
                 style={{ borderColor: 'hsl(var(--border))' }}>
              <span>New price</span>
              <span className="inline-flex items-center font-mono"
                    style={{ color: delta >= 0 ? '#10b981' : 'hsl(0 70% 55%)' }}>
                <IndianRupee className="h-3.5 w-3.5" />{previewPrice != null ? previewPrice.toFixed(2) : '—'}
              </span>
            </div>
            {delta !== 0 && previewPrice != null && (
              <div className="text-[11px] pt-1" style={{ color: 'hsl(var(--muted-foreground))' }}>
                {delta >= 0 ? '+' : ''}₹{delta.toFixed(2)} vs base
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleCancel} disabled={submitting}
                  data-testid="slot-peak-cancel">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || previewPrice == null}
                  data-testid="slot-peak-confirm">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <TrendingUp className="h-4 w-4" />}
            Apply peak pricing
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
